
/**
 * Converts a tool name from snake_case or kebab-case to human-readable format.
 * Normalizes the input by replacing separators and capitalizing each word.
 *
 * @param {string} name - The tool name to humanize (e.g., "document_search" or "generate-summary")
 * @returns {string} Human-readable tool name with proper capitalization (e.g., "Document Search")
 *
 * @example
 * // Returns "Document Search"
 * humanizeToolName("document_search");
 *
 * @example
 * // Returns "Generate Summary"
 * humanizeToolName("generate-summary");
 *
 * @example
 * // Handles empty/invalid input
 * humanizeToolName("") // Returns "tool"
 */
export const humanizeToolName = (name: string): string => {
  if (!name) return "tool";
  // Replace underscores and hyphens with spaces, normalize whitespace
  const n = name.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  // Capitalize each word
  return n
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/**
 * Formats a duration in milliseconds to a human-readable string.
 * Displays minutes and seconds in M:SS format when minutes are present,
 * or just seconds with "s" suffix when under one minute.
 *
 * @param {number} ms - Duration in milliseconds (must be a finite positive number)
 * @returns {string} Formatted duration string (e.g., "1m30s" for 90000ms, "45s" for 45000ms)
 *
 * @example
 * // Returns "1m30s"
 * formatDuration(90000);
 *
 * @example
 * // Returns "45s"
 * formatDuration(45000);
 *
 * @example
 * // Handles edge cases
 * formatDuration(-100)    // Returns "0s"
 * formatDuration(0)       // Returns "0s"
 * formatDuration(Infinity) // Returns "0s"
 */
export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m${seconds.toString().padStart(2, "0")}s`;
};

/**
 * Utility functions for parsing AI JSON responses consistently across the application.
 * These utilities handle both complete JSON objects and partial streaming JSON responses.
 */

/**
 * Extracts the answer content from an AI JSON response string.
 * Handles both complete JSON objects and partial streaming JSON by attempting
 * JSON parsing first, then falling back to regex extraction for incomplete JSON.
 *
 * @param {string} content - The content string to parse (can be partial JSON during streaming)
 * @returns {string} The extracted answer content from the JSON object's "answer" field,
 *                   or the original string if parsing fails
 *
 * @example
 * // Complete JSON response
 * extractAnswerFromJSON('{"answer":"Hello world"}')
 * // Returns "Hello world"
 *
 * @example
 * // Partial/streaming JSON response
 * extractAnswerFromJSON('{"answer":"Streaming respo')
 * // Returns "Streaming respo"
 *
 * @example
 * // Non-JSON content passes through unchanged
 * extractAnswerFromJSON("Regular text response")
 * // Returns "Regular text response"
 *
 * @note This function handles JSON escape sequences like \", \\, \n, \r, \t
 * @note Currently only supports the "answer" key - other keys are not extracted
 */
export function extractAnswerFromJSON(content: string): string {
  const trimmed = content.trim();

  // TODO: Cater for other JSON keys than "answer" such as "content", "response", "data",etc.
  // Quick check: if it doesn't look like JSON, return as-is
  if (!trimmed.startsWith("{\"") && !trimmed.includes("\"answer\"")) {
    return content;
  }

  // Try to parse as complete JSON first
  if (trimmed.startsWith("{\"") && (trimmed.endsWith("\"}") || trimmed.endsWith("\"}"))) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.answer && typeof parsed.answer === "string") {
        return parsed.answer;
      }
    } catch {
      // Fall through to regex parsing
    }
  }

  // If complete JSON parsing fails, try regex extraction for partial/streaming JSON
  if (trimmed.includes("\"answer\"")) {
    try {
      // Look for "answer":"..." pattern in the string
      const answerMatch = trimmed.match(/"answer"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (answerMatch && answerMatch[1]) {
        // Decode JSON escapes in the answer
        return answerMatch[1]
          .replace(/\\"/g, "\"")
          .replace(/\\\\/g, "\\")
          .replace(/\\n/g, "\n")
          .replace(/\\r/g, "\r")
          .replace(/\\t/g, "\t");
      }
    } catch {
      // If regex parsing also fails, return original
    }
  }

  return content;
}

/**
 * Checks if a string appears to be a JSON response from the AI.
 * Performs a quick pattern check to identify JSON-formatted responses
 * without full parsing.
 *
 * @param {string} content - The content string to check
 * @returns {boolean} True if the content appears to be an AI JSON response
 *                    (starts with "{\"" and contains "\"answer\"" field)
 *
 * @example
 * // Valid AI JSON response
 * isJSONResponse('{"answer":"Hello"}') // Returns true
 *
 * @example
 * // Invalid/non-JSON content
 * isJSONResponse("Plain text") // Returns false
 * isJSONResponse('{"data":"value"}') // Returns false (no "answer" field)
 */
export function isJSONResponse(content: string): boolean {
  const trimmed = content.trim();
  return trimmed.startsWith("{\"") && trimmed.includes("\"answer\"");
}

/**
 * Creates a new StreamSession object with initialized default values.
 * Used to set up a fresh streaming session for handling SSE responses.
 *
 * @returns {StreamSession} A new stream session object with all flags and buffers reset
 *
 * @example
 * const session = createStreamSession();
 * // session.buffer === ""
 * // session.thinkingActive === false
 * // session.abortController === null
 */
export function createStreamSession(): StreamSession {
  return {
    abortController: null,
    reader: null,
    buffer: "",
    thinkingActive: false,
    answerToolActive: false,
    answerJsonStream: "",
    answerHasStreamed: false,
    hasError: false,
    activeMessageToolRunId: null,
  };
}

/**
 * Resets all state flags in a stream session while preserving the abort controller and reader.
 * Used between message processing cycles to clear accumulated state.
 *
 * @param {StreamSession} session - The stream session object to reset
 * @returns {void}
 *
 * @example
 * resetSessionFlags(session);
 * // Clears buffer, thinking state, answer state, and error flags
 * // Does NOT clear abortController or reader
 */
export function resetSessionFlags(session: StreamSession): void {
  session.buffer = "";
  session.thinkingActive = false;
  session.answerToolActive = false;
  session.answerJsonStream = "";
  session.answerHasStreamed = false;
  session.hasError = false;
  session.activeMessageToolRunId = null; // reset stream owner on new question
}

/**
 * Completely resets a stream session, clearing all state including the abort controller.
 * Use this when ending a stream completely, not just between messages.
 *
 * @param {StreamSession} session - The stream session object to reset
 * @returns {void}
 *
 * @example
 * resetStreamSession(session);
 * // Clears all streaming state and network resources
 */
export function resetStreamSession(session: StreamSession): void {
  session.abortController = null;
  session.reader = null;
  resetSessionFlags(session);
}

/**
 * Normalizes an authentication token to the `Bearer` format expected by the backend.
 * Prepends "Bearer " prefix if not already present.
 *
 * @param {string | null | undefined} rawToken - The raw authentication token
 * @returns {string} The normalized token in "Bearer <token>" format, or empty string if no token provided
 *
 * @example
 * // Token without Bearer prefix
 * buildAuthHeader("eyJhbGc...") // Returns "Bearer eyJhbGc..."
 *
 * @example
 * // Token already with Bearer prefix
 * buildAuthHeader("Bearer eyJhbGc...") // Returns "Bearer eyJhbGc..."
 *
 * @example
 * // Missing or null token
 * buildAuthHeader(null) // Returns ""
 * buildAuthHeader(undefined) // Returns ""
 */
function buildAuthHeader(rawToken: string | null | undefined): string {
  if (!rawToken) return "";
  return rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
}

/**
 * Retrieves the authentication token from either a provided getter function or session storage,
 * and returns it as a normalized Bearer authorization header.
 *
 * @param {Function} [getAuthToken] - Optional getter function that returns the auth token.
 *                                    If not provided, falls back to reading from sessionStorage["token"]
 * @returns {string} The normalized authorization header value (e.g., "Bearer <token>"),
 *                   or empty string if no token is found
 *
 * @example
 * // Using getter function
 * getAuthHeader(() => "mytoken123")
 * // Returns "Bearer mytoken123"
 *
 * @example
 * // Using session storage (when no getter provided)
 * sessionStorage.setItem("token", "stored-token");
 * getAuthHeader()
 * // Returns "Bearer stored-token"
 *
 * @example
 * // No token available
 * getAuthHeader(() => null)
 * // Returns ""
 */
export function getAuthHeader(getAuthToken?: () => string | null | undefined): string {
  const raw = getAuthToken?.() ?? sessionStorage.getItem("token") ?? "";
  return buildAuthHeader(raw);
}

/**
 * Parses a Server-Sent Events (SSE) character buffer into discrete event blocks.
 * Splits the buffer by double newlines (SSE delimiter), extracts event metadata,
 * and collects data lines. Returns any trailing incomplete block as remainder.
 *
 * @param {string} buffer - The SSE character buffer to parse
 * @returns {{events: ParsedSSEBlock[], remainder: string}} Object containing:
 *          - events: Array of parsed SSE blocks with eventName, dataStr, and eventId
 *          - remainder: Trailing incomplete block (will be prepended to next buffer)
 *
 * @example
 * const buffer = 'event: message\nid: 123\ndata: {"answer":"hello"}\n\nevent: done\ndata:';
 * const { events, remainder } = parseSSEBlocks(buffer);
 * // events = [
 * //   { eventName: "message", eventId: "123", dataStr: '{"answer":"hello"}' },
 * //   { eventName: "done", eventId: null, dataStr: "" }  // Skipped due to no data
 * // ]
 * // remainder = ""
 *
 * @note Each SSE event is separated by a blank line (\n\n)
 * @note Lines are prefixed with "event:", "id:", or "data:"
 * @note Blocks with no data content are skipped
 * @note Multi-line data fields are joined with newlines
 */
export function parseSSEBlocks(buffer: string): { events: ParsedSSEBlock[]; remainder: string } {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop() ?? "";
  const events: ParsedSSEBlock[] = [];

  for (const block of parts) {
    const lines = block.split("\n");
    let eventName: string | null = null;
    let eventId: string | null = null;
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith("id:")) {
        eventId = line.slice(3).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    const dataStr = dataLines.join("\n");
    if (!dataStr) continue;
    events.push({ eventName, dataStr, eventId });
  }

  return { events, remainder };
}

/**
 * Safe wrapper around JSON.parse that catches parsing errors gracefully.
 * Returns null instead of throwing exceptions on malformed JSON.
 *
 * @template T - The expected type of the parsed JSON object
 * @param {string} value - The JSON string to parse
 * @returns {T | null} The parsed object of type T, or null if parsing fails
 *
 * @example
 * // Valid JSON
 * const data = tryParseJSON<{name: string}>('{"name":"John"}');
 * // Returns {name: "John"}
 *
 * @example
 * // Invalid JSON
 * const data = tryParseJSON<any>('not valid json');
 * // Returns null
 *
 * @example
 * // With type inference
 * interface User { id: number; email: string; }
 * const user = tryParseJSON<User>('{"id":1,"email":"test@example.com"}');
 * // Returns {id: 1, email: "test@example.com"} as User
 */
export function tryParseJSON<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * Extracts the textual body/content from mixed response payloads.
 * Handles string content (extracts from JSON if applicable) and object content
 * with nested content properties.
 *
 * @param {unknown} content - The response payload (can be string, object, or other type)
 * @returns {string} The extracted textual content, or empty string if no content found
 *
 * @example
 * // String payload (JSON formatted)
 * extractBodyFromContent('{"answer":"Hello world"}')
 * // Returns "Hello world"
 *
 * @example
 * // String payload (plain text)
 * extractBodyFromContent("Plain text response")
 * // Returns "Plain text response"
 *
 * @example
 * // Object payload with content property
 * extractBodyFromContent({ content: "Text from object" })
 * // Returns "Text from object"
 *
 * @example
 * // Object payload with metadata
 * extractBodyFromContent({ content: "Main content", metadata: "some metadata" })
 * // Returns "Main content"
 *
 * @example
 * // Invalid/missing content
 * extractBodyFromContent(null) // Returns ""
 * extractBodyFromContent({}) // Returns ""
 * extractBodyFromContent(123) // Returns ""
 *
 * @note This function uses extractAnswerFromJSON for consistent JSON parsing
 * @note The shape { content: string; metadata?: string } is the known backend response format
 */
export function extractBodyFromContent(content: unknown): string {
  if (typeof content === "string") {
    // Use the shared JSON parser for consistent handling
    return extractAnswerFromJSON(content);
  }
  if (content && typeof content === "object" && "content" in content) {
    // Known shape from backend: { content: string; metadata?: string }
    const contentValue = (content as any).content;
    return typeof contentValue === "string" ? contentValue : "";
  }
  return "";
}
